/**
 * Ralph & Doug: "Blick ins Buch", flipbook with arrows and fullscreen reader.
 *
 * Ported from the homepage prototype (rd-home/index.html). One instance per .rd-flip section.
 *
 * Flipbook: the cover opens by itself the first time the book is on screen; after that the
 * arrows beside the book (or the arrow keys) step through the spreads, on phones page by
 * page. Each step animates a position x between 0 (closed) and TURNS (one unit per leaf).
 * The last turn lands on a blurred spread and fades in the end-of-preview card.
 * ?book=<0..N> shows spread N without the opening animation, for review screenshots.
 *
 * Reader: pages come from the flipbook itself (cover, leaf backs and fronts, the blurred base),
 * so both always show the same preview. Phone shows one page per view, wider screens spreads.
 */
(function () {
  'use strict';

  var easeInOut = function (t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  };
  var clamp = function (v) {
    return Math.min(Math.max(v, 0), 1);
  };
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var phone = window.matchMedia('(max-width: 749px)');

  var setupFlip = function (section) {
    var book = section.querySelector('.rd-book');
    var body = section.querySelector('.rd-book__body');
    var floor = section.querySelector('.rd-book__floor');
    var endEl = section.querySelector('.rd-book__end');
    var leaves = Array.prototype.slice.call(section.querySelectorAll('.rd-leaf'));
    var castR = section.querySelector('.rd-book__cast--right');
    var castL = section.querySelector('.rd-book__cast--left');
    var viewer = section.querySelector('.rd-flip__viewer');
    var prevBtn = section.querySelector('.rd-flip__nav--prev');
    var nextBtn = section.querySelector('.rd-flip__nav--next');
    var status = section.querySelector('.rd-flip__status');
    if (!book || !body || !floor || !castR || !castL || leaves.length === 0) return;

    var TURNS = leaves.length;
    var PAGES = 2 * TURNS + 1;
    var TURN_MS = 950;
    var PAN_MS = 520;
    var OPEN_DELAY = 250;
    var frozen = new URLSearchParams(window.location.search).get('book');

    // Draws the book at position x (0 = closed, n = leaf n just landed, TURNS = end of the
    // preview) with the camera at `shift`, in % of the book width: -25 shows the right page
    // (or the closed cover), +25 the left page, 0 the whole spread.
    var render = function (x, shift) {
      var turn = Math.min(Math.floor(x), TURNS - 1);
      var f = x >= TURNS ? 1 : x - turn;
      var open = 0;
      leaves.forEach(function (leaf, i) {
        var fi = i < turn ? 1 : i > turn ? 0 : f;
        if (i === 0) open = fi;
        leaf.style.setProperty('--a', (-180 * fi).toFixed(2));
        // On the right the first leaf lies on top, on the left the last one; the leaf in the air is above all.
        leaf.style.zIndex = fi > 0 && fi < 1 ? 50 : fi < 0.5 ? 20 + (TURNS - i) : 20 + i;
        leaf.style.setProperty('--sf', fi < 0.5 ? (fi * 2).toFixed(3) : '0');
        leaf.style.setProperty('--sb', fi >= 0.5 ? ((1 - fi) * 2).toFixed(3) : '0');
      });
      // Cast shadows follow the leaf in the air: on the right page while it rises, on the left while it lands.
      var mf = f > 0 && f < 1 ? f : 0;
      castR.style.zIndex = castL.style.zIndex = 49;
      castR.style.setProperty('--cr-s', mf < 0.5 ? (1 - mf * 2).toFixed(3) : '0');
      castR.style.setProperty('--cr-o', mf > 0 && mf < 0.5 ? (Math.sin(mf * Math.PI) * 0.9).toFixed(3) : '0');
      castL.style.setProperty('--cl-s', mf >= 0.5 ? ((mf - 0.5) * 2).toFixed(3) : '0');
      castL.style.setProperty('--cl-o', mf >= 0.5 && mf < 1 ? (Math.sin(mf * Math.PI) * 0.9).toFixed(3) : '0');
      body.style.setProperty('--shift', shift.toFixed(2));
      floor.style.setProperty('--shift', shift.toFixed(2));
      floor.style.setProperty('--floor', (0.55 + 0.45 * open).toFixed(3));
      section.style.setProperty('--open', open.toFixed(3));
      section.dataset.spread = String(Math.round(x));
      // The left page block and the gutter only exist once the cover lies flat on the left.
      section.style.setProperty('--landed', clamp((open - 0.92) / 0.08).toFixed(3));
      // End card: fades in over the last stretch of the final turn.
      var end = turn === TURNS - 1 ? clamp((f - 0.7) / 0.3) : 0;
      book.style.setProperty('--end', end.toFixed(3));
      book.classList.toggle('is-end', end > 0.5);
      // Until it shows, the card and its link stay out of the tab order and the accessibility tree.
      if (endEl && endEl.inert !== end <= 0.5) endEl.inert = end <= 0.5;
    };

    // Resting positions the arrows step through. Wide screens: the closed cover, then each
    // spread. Phones: the cover, then the left and the right page of each spread, then the
    // end card centred on the last spread.
    var buildStops = function () {
      var list = [{ x: 0, shift: -25 }];
      for (var k = 1; k < TURNS; k++) {
        if (phone.matches) {
          list.push({ x: k, shift: 25 }, { x: k, shift: -25 });
        } else {
          list.push({ x: k, shift: 0 });
        }
      }
      list.push({ x: TURNS, shift: 0 });
      return list;
    };

    // Camera while moving between two stops.
    var shiftBetween = function (from, to, x, e) {
      if (!phone.matches) return -25 * (1 - Math.min(x, 1));
      if (from.x === to.x) return from.shift + (to.shift - from.shift) * e;
      // A leaf in the air: follow it from the right page (or the cover) to the left page,
      // or to the centred end card on the last turn.
      var turn = Math.min(from.x, to.x);
      return -25 + (turn === TURNS - 1 ? 25 : 50) * (x - turn);
    };

    var describe = function (stop) {
      if (stop.x === 0) return 'Buchcover';
      if (stop.x === TURNS) return 'Ende der Leseprobe';
      var left = 2 * stop.x;
      if (stop.shift > 0) return 'Seite ' + left + ' von ' + PAGES;
      if (stop.shift < 0) return 'Seite ' + (left + 1) + ' von ' + PAGES;
      return 'Seiten ' + left + ' und ' + (left + 1) + ' von ' + PAGES;
    };

    var stops = buildStops();
    var at = 0; // stop the book rests on
    var target = 0; // stop the arrows asked for
    var running = false;
    var opened = false;

    var updateUi = function () {
      if (prevBtn) prevBtn.disabled = target === 0;
      if (nextBtn) nextBtn.disabled = target === stops.length - 1;
    };

    // Moves one stop at a time towards the target, so a quick double click turns two pages.
    var run = function () {
      if (running) return;
      if (at === target) {
        if (status) status.textContent = describe(stops[at]);
        return;
      }
      var dir = target > at ? 1 : -1;
      var from = stops[at];
      var to = stops[at + dir];
      var duration = reduce.matches ? 0 : from.x === to.x ? PAN_MS : TURN_MS;
      var start = null;
      running = true;
      var frame = function (now) {
        if (start === null) start = now;
        var t = duration ? Math.min(1, (now - start) / duration) : 1;
        var e = easeInOut(t);
        var x = from.x + (to.x - from.x) * e;
        render(x, t >= 1 ? to.shift : shiftBetween(from, to, x, e));
        if (t < 1) {
          window.requestAnimationFrame(frame);
          return;
        }
        at += dir;
        running = false;
        run();
      };
      window.requestAnimationFrame(frame);
    };

    var go = function (delta) {
      opened = true;
      target = Math.max(0, Math.min(stops.length - 1, target + delta));
      updateUi();
      run();
    };

    if (prevBtn) prevBtn.addEventListener('click', function () { go(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { go(1); });
    (viewer || book).addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      }
    });

    // Switching between phone and wide layout keeps the same spread.
    phone.addEventListener('change', function () {
      var x = stops[target].x;
      stops = buildStops();
      for (var i = 0; i < stops.length; i++) {
        if (stops[i].x === x) break;
      }
      at = target = Math.min(i, stops.length - 1);
      render(stops[at].x, stops[at].shift);
      updateUi();
      if (status) status.textContent = describe(stops[at]);
    });

    // Warm up the pages before the section arrives, so the first turn never reveals a blank.
    var imgs = section.querySelectorAll('img');
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries, obs) {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        Array.prototype.forEach.call(imgs, function (im) {
          im.loading = 'eager';
          if (im.decode) im.decode().catch(function () {});
        });
      }, { rootMargin: '150% 0px' }).observe(section);
    }

    if (frozen !== null) {
      var fx = Math.min(Math.max(Math.round(parseFloat(frozen) || 0), 0), TURNS);
      for (var j = 0; j < stops.length && stops[j].x !== fx; j++);
      at = target = Math.min(j, stops.length - 1);
      opened = true;
    }
    render(stops[at].x, stops[at].shift);
    updateUi();
    if (status) status.textContent = describe(stops[at]);

    // The cover opens by itself the first time most of the book is on screen. Watched on the
    // viewer, not the book: on phones the book is twice as wide as the screen, so less than
    // 60 % of it can ever be visible.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries, obs) {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        if (opened) return;
        window.setTimeout(function () {
          if (!opened && target === 0) go(1);
        }, OPEN_DELAY);
      }, { threshold: 0.6 }).observe(viewer || book);
    } else {
      go(1);
    }
  };

  var setupReader = function (section) {
    var book = section.querySelector('.rd-book');
    var dialog = section.querySelector('.rd-reader');
    if (!book || !dialog || !dialog.showModal) return;
    var viewEl = dialog.querySelector('.rd-reader__view');
    var spreadEl = dialog.querySelector('.rd-reader__spread');
    var prevBtn = dialog.querySelector('.rd-reader__nav--prev');
    var nextBtn = dialog.querySelector('.rd-reader__nav--next');
    var closeBtn = dialog.querySelector('.rd-reader__close');
    var endCard = book.querySelector('.rd-book__end-card');
    if (!viewEl || !spreadEl || !prevBtn || !nextBtn || !closeBtn) return;
    var wide = window.matchMedia('(min-width: 750px)');
    var MAX_ZOOM = 4;

    // Page order as printed: cover, then each leaf back and the next leaf front, the blurred base last.
    var pages = [];
    Array.prototype.forEach.call(book.querySelectorAll('.rd-leaf'), function (leaf) {
      pages.push(leaf.querySelector('.rd-leaf__front img'), leaf.querySelector('.rd-leaf__back img'));
    });
    pages.push(book.querySelector('.rd-book__base img'));
    pages = pages.filter(Boolean);
    if (pages.length < 2) return;
    var LAST = pages.length - 1; // the two blurred pages are pages[LAST - 1] and pages[LAST]

    // A view is a list of page indexes plus whether the end card sits on it.
    var buildViews = function () {
      var v = [];
      if (wide.matches) {
        v.push({ pages: [0] });
        for (var i = 1; i < LAST - 1; i += 2) v.push({ pages: [i, i + 1] });
        v.push({ pages: [LAST - 1, LAST], end: true });
      } else {
        for (var j = 0; j < LAST - 1; j++) v.push({ pages: [j] });
        v.push({ pages: [LAST - 1], end: true });
      }
      return v;
    };
    var views = buildViews();
    var current = 0;

    // Zoom state: scale s and offset (tx, ty) from the centred position.
    var s = 1;
    var tx = 0;
    var ty = 0;
    var apply = function (settle) {
      spreadEl.classList.toggle('is-settling', !!settle);
      spreadEl.style.transform = 'translate(' + tx + 'px, ' + ty + 'px) scale(' + s + ')';
    };
    var clampPan = function () {
      var r = spreadEl.getBoundingClientRect();
      var w = r.width / s;
      var h = r.height / s;
      var mx = Math.max(0, (w * s - viewEl.clientWidth) / 2);
      var my = Math.max(0, (h * s - viewEl.clientHeight) / 2);
      tx = Math.min(mx, Math.max(-mx, tx));
      ty = Math.min(my, Math.max(-my, ty));
    };
    var resetZoom = function (settle) {
      s = 1;
      tx = 0;
      ty = 0;
      apply(settle);
    };

    var render = function () {
      var view = views[current];
      spreadEl.textContent = '';
      spreadEl.classList.toggle('rd-reader__spread--single', view.pages.length === 1);
      view.pages.forEach(function (i) {
        var img = new Image();
        img.src = pages[i].currentSrc || pages[i].getAttribute('src');
        img.alt = pages[i].alt;
        img.width = 1200;
        img.height = 1200;
        img.draggable = false;
        spreadEl.appendChild(img);
      });
      if (view.end && endCard) spreadEl.appendChild(endCard.cloneNode(true));
      prevBtn.disabled = current === 0;
      nextBtn.disabled = current === views.length - 1;
      resetZoom(false);
    };
    var go = function (d) {
      var n = Math.min(views.length - 1, Math.max(0, current + d));
      if (n === current) return;
      current = n;
      render();
    };

    // Open at the spread the scroll book rests on (phone: its left page).
    var open = function () {
      views = buildViews();
      var spread = Math.min(views.length, Math.max(0, parseInt(section.dataset.spread || '0', 10)));
      current = wide.matches
        ? Math.min(spread, views.length - 1)
        : spread === 0 ? 0 : Math.min(2 * spread - 1, views.length - 1);
      render();
      document.documentElement.style.overflow = 'hidden';
      dialog.showModal();
    };
    book.addEventListener('click', function (e) {
      if (!e.target.closest('a')) open();
    });
    book.addEventListener('keydown', function (e) {
      // Only the book itself opens the reader; Enter on the end card's link follows the link.
      if (e.target !== book) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        open();
      }
    });
    dialog.addEventListener('close', function () {
      document.documentElement.style.overflow = '';
      book.focus({ preventScroll: true });
    });
    closeBtn.addEventListener('click', function () {
      dialog.close();
    });
    prevBtn.addEventListener('click', function () {
      go(-1);
    });
    nextBtn.addEventListener('click', function () {
      go(1);
    });
    dialog.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      }
    });
    wide.addEventListener('change', function () {
      if (dialog.open) {
        views = buildViews();
        current = Math.min(current, views.length - 1);
        render();
      }
    });
    // The end card's link closes the reader before it jumps.
    spreadEl.addEventListener('click', function (e) {
      if (e.target.closest('a')) dialog.close();
    });

    // Pointer gestures: two fingers pinch (zoom around the midpoint, pan with it), one finger pans a
    // zoomed page or swipes to the next/previous view when not zoomed.
    var pts = new Map();
    var start = null;
    // Resting centre of the page block (its transform moves the centre by exactly tx, ty).
    var centre = function () {
      var r = spreadEl.getBoundingClientRect();
      return { x: r.left + r.width / 2 - tx, y: r.top + r.height / 2 - ty };
    };
    var mid = function () {
      var a = Array.from(pts.values());
      return { x: (a[0].x + a[1].x) / 2, y: (a[0].y + a[1].y) / 2, d: Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y) };
    };
    var begin = function () {
      if (pts.size === 2) {
        start = { kind: 'pinch', s: s, tx: tx, ty: ty, m: mid() };
      } else if (pts.size === 1) {
        var p = Array.from(pts.values())[0];
        start = { kind: 'pan', s: s, tx: tx, ty: ty, x: p.x, y: p.y };
      } else {
        start = null;
      }
    };
    viewEl.addEventListener('pointerdown', function (e) {
      if (e.target.closest('a, button')) return;
      try {
        viewEl.setPointerCapture(e.pointerId);
      } catch (err) {
        /* synthetic or already released pointer */
      }
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      begin();
    });
    viewEl.addEventListener('pointermove', function (e) {
      if (!pts.has(e.pointerId) || !start) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      var c = centre();
      if (start.kind === 'pinch' && pts.size === 2) {
        var m = mid();
        s = Math.min(MAX_ZOOM, Math.max(1, (start.s * m.d) / start.m.d));
        // Keep the page point that was under the starting midpoint under the current midpoint.
        var px = (start.m.x - c.x - start.tx) / start.s;
        var py = (start.m.y - c.y - start.ty) / start.s;
        tx = m.x - c.x - s * px;
        ty = m.y - c.y - s * py;
        clampPan();
        apply(false);
      } else if (start.kind === 'pan' && s > 1) {
        var p = pts.get(e.pointerId);
        tx = start.tx + p.x - start.x;
        ty = start.ty + p.y - start.y;
        clampPan();
        apply(false);
      }
    });
    var end = function (e) {
      if (!pts.has(e.pointerId)) return;
      var p = pts.get(e.pointerId);
      if (start && start.kind === 'pan' && start.s === 1 && pts.size === 1) {
        var dx = p.x - start.x;
        var dy = p.y - start.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) go(dx < 0 ? 1 : -1);
      }
      pts.delete(e.pointerId);
      if (pts.size === 0 && s < 1.05) resetZoom(true);
      begin();
    };
    viewEl.addEventListener('pointerup', end);
    viewEl.addEventListener('pointercancel', end);
    // Trackpad pinch arrives as ctrl + wheel; zoom around the cursor.
    viewEl.addEventListener(
      'wheel',
      function (e) {
        if (!e.ctrlKey) return;
        e.preventDefault();
        var c = centre();
        var ns = Math.min(MAX_ZOOM, Math.max(1, s * Math.exp(-e.deltaY * 0.01)));
        var px = (e.clientX - c.x - tx) / s;
        var py = (e.clientY - c.y - ty) / s;
        s = ns;
        tx = e.clientX - c.x - s * px;
        ty = e.clientY - c.y - s * py;
        if (s === 1) {
          tx = 0;
          ty = 0;
        }
        clampPan();
        apply(false);
      },
      { passive: false }
    );
    // Safari's own page pinch would fight ours inside the reader.
    ['gesturestart', 'gesturechange'].forEach(function (t) {
      viewEl.addEventListener(t, function (e) {
        e.preventDefault();
      });
    });
  };

  var init = function () {
    Array.prototype.forEach.call(document.querySelectorAll('.rd-flip'), function (section) {
      if (section.dataset.rdFlipReady) return;
      section.dataset.rdFlipReady = '1';
      setupFlip(section);
      setupReader(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // The theme editor re-renders a section after a setting change.
  document.addEventListener('shopify:section:load', init);
})();
